'use client';

/**
 * OTT-style search — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/LiveSearchBox.tsx}.
 *
 * <h3>Two operating modes</h3>
 * <ul>
 *   <li><b>Suggest mode</b> (default) — debounced suggest calls via the
 *       host {@link SearchSource} adapter.  Dropdown shows thumbnails +
 *       titles.  Requires the host to mount a {@code SearchSourceProvider}.</li>
 *   <li><b>Catalog mode</b> — activated when {@code catalogSearch} prop
 *       is provided (sourced from {@code shell.search} in the admin Shell
 *       editor).  Makes a direct {@code POST catalog/search} call and
 *       shows results grouped by type (courses, news, FAQ, …) in tabs.
 *       No {@code SearchSource} adapter required.</li>
 * </ul>
 *
 * <h3>Two visual modes via {@code variant}</h3>
 * <ul>
 *   <li>{@code 'inline'}  — pill in the header; dropdown below.</li>
 *   <li>{@code 'overlay'} — full-width modal at the top of the viewport.</li>
 * </ul>
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import {
  useSearchSource,
  useMessages,
  courseDetailHref,
  catalogSearchHref,
  CATALOG_BASE,
  type SearchSuggestion,
  t,
} from '../../portal';
import type { SearchSectionConfig, LocalizedString, ShellMessages } from '../../portal';
import {
  SbCommandDialog,
  SbCommandInput,
  SbCommandTabBar,
  SbCommandEmpty,
  SbCommandGroup,
  SbCommandItem,
  SbCommandSeeAll,
  type SbCommandTab,
} from './SbCommand';

const RECENTS_STORAGE_KEY_PREFIX = 'lms-portal-search-recents:';
const MAX_RECENTS_DISPLAYED = 8;
const SUGGEST_DEBOUNCE_MS = 200;
const CATALOG_DEBOUNCE_MS = 300;

// =============================================================================
// Catalog API types
// =============================================================================

interface CatalogDoc {
  id: number;
  title?: string;
  name?: string;
  surname?: string;
  question?: string;
  description?: string;
  image?: string;
  thumbnail?: string;
  avatarUrl?: string;
  key?: string;
  docType: string;
}

interface CatalogSection {
  totalRecords: number;
  documents: CatalogDoc[];
}

interface CatalogResponse {
  expandedDocuments: {
    course?: CatalogSection;
    blog?: CatalogSection;
    news?: CatalogSection;
    faq?: CatalogSection;
    document?: CatalogSection;
    page?: CatalogSection;
    employee?: CatalogSection;
  };
}

type TabId = 'all' | 'course' | 'blog' | 'news' | 'faq' | 'document' | 'page' | 'employee';

interface TabDef {
  id: TabId;
  label: string;
  count: number;
}

// =============================================================================
// Labels — resolved from admin-configured cfg.labels (no built-in defaults)
// =============================================================================

interface ResolvedLabels {
  placeholder: string;
  allResults: string;
  courses: string;
  blog: string;
  news: string;
  faq: string;
  documents: string;
  pages: string;
  employees: string;
  seeAllCourses: string;
  seeAllBlog: string;
  seeAllNews: string;
  seeAllFaq: string;
  seeAllDocuments: string;
  seeAllPages: string;
  seeAllEmployees: string;
  noResults: string;
  typeToSearch: string;
  searching: string;
  close: string;
  clearSearch: string;
}

const DEFAULT_LABELS: Record<string, Record<string, string>> = {
  placeholder: { tr: 'Ara…', en: 'Search…' },
  allResults: { tr: 'Tüm sonuçlar', en: 'All results' },
  courses: { tr: 'Eğitimler', en: 'Courses' },
  blog: { tr: 'Blog', en: 'Blog' },
  news: { tr: 'Haberler', en: 'News' },
  faq: { tr: 'SSS', en: 'FAQ' },
  documents: { tr: 'Belgeler', en: 'Documents' },
  pages: { tr: 'Sayfalar', en: 'Pages' },
  employees: { tr: 'Çalışanlar', en: 'Employees' },
  seeAllCourses: { tr: 'Tümünü gör', en: 'See all' },
  seeAllBlog: { tr: 'Tümünü gör', en: 'See all' },
  seeAllNews: { tr: 'Tümünü gör', en: 'See all' },
  seeAllFaq: { tr: 'Tümünü gör', en: 'See all' },
  seeAllDocuments: { tr: 'Tümünü gör', en: 'See all' },
  seeAllPages: { tr: 'Tümünü gör', en: 'See all' },
  seeAllEmployees: { tr: 'Tümünü gör', en: 'See all' },
  noResults: { tr: 'Sonuç bulunamadı', en: 'No results' },
  typeToSearch: { tr: 'Aramak için yazın…', en: 'Type to search…' },
  searching: { tr: 'Yükleniyor…', en: 'Loading…' },
  close: { tr: 'Kapat', en: 'Close' },
  clearSearch: { tr: 'Temizle', en: 'Clear' },
};

/** Resolve a locale-keyed default label: active locale → English → ''. */
function defaultLabel(key: string, locale: string): string {
  return DEFAULT_LABELS[key]?.[locale] ?? DEFAULT_LABELS[key]?.['en'] ?? '';
}

/**
 * Suggest-mode static UI strings (placeholders, dropdown headers, empty
 * states). Not admin-configurable — the built-in dictionary here is the
 * bottom of the fallback chain. A host may override any of these by
 * injecting a {@link ShellMessages} catalog (keyed {@code 'search.<key>'})
 * via {@code MessagesProvider}; {@link resolveSuggestLabels} applies that
 * override on top of these defaults.
 */
function tr(key: string, locale: string): string {
  const dict: Record<string, Record<string, string>> = {
    search: { tr: 'Ara…', en: 'Search…' },
    clearSearch: { tr: 'Aramayı temizle', en: 'Clear search' },
    startTyping: { tr: 'Öneriler için yazmaya başlayın.', en: 'Start typing to see suggestions.' },
    recentSearches: { tr: 'Son aramalar', en: 'Recent searches' },
    trending: { tr: 'Popüler', en: 'Trending' },
    clear: { tr: 'Temizle', en: 'Clear' },
  };
  return dict[key]?.[locale] ?? dict[key]?.['en'] ?? key;
}

/** "No suggestions for X" empty state — interpolated, so kept separate. */
function noSuggestionsText(query: string, locale: string): string {
  return locale === 'tr'
    ? `"${query}" için öneri yok. Aramak için Enter'a basın.`
    : `No suggestions for "${query}". Press Enter to search anyway.`;
}

/** All static suggest-mode strings pre-resolved for one render, so the
 *  dropdown sub-components stay free of locale / message plumbing. */
interface SuggestLabels {
  placeholder: string;
  clearSearch: string;
  startTyping: string;
  recentSearches: string;
  trending: string;
  clearRecents: string;
  /** Interpolated empty state — pass the current query. */
  noSuggestions: (query: string) => string;
}

/** Resolve suggest-mode strings: host-injected message → built-in default. */
function resolveSuggestLabels(messages: ShellMessages | null, locale: string): SuggestLabels {
  const m = (key: string, fallback: string) => messages?.[`search.${key}`] ?? fallback;
  return {
    placeholder: m('search', tr('search', locale)),
    clearSearch: m('clearSearch', tr('clearSearch', locale)),
    startTyping: m('startTyping', tr('startTyping', locale)),
    recentSearches: m('recentSearches', tr('recentSearches', locale)),
    trending: m('trending', tr('trending', locale)),
    clearRecents: m('clear', tr('clear', locale)),
    noSuggestions: (query: string) => {
      const tmpl = messages?.['search.noSuggestions'];
      return tmpl ? tmpl.replace('{query}', query) : noSuggestionsText(query, locale);
    },
  };
}

function resolveLabels(
  cfg: SearchSectionConfig,
  locale: string,
  fallback: string,
  messages: ShellMessages | null,
): ResolvedLabels {
  const lbl = cfg.labels ?? {};
  // Precedence per key: admin-configured label → host-injected message →
  // built-in default for the active locale.
  const r = (key: keyof typeof lbl) =>
    lbl[key] !== undefined
      ? t(lbl[key]!, locale, fallback)
      : (messages?.[`search.${key as string}`] ?? defaultLabel(key as string, locale));
  // "See all" text: prefer section.hrefText, fall back to labels.seeAllX, then default
  const seeAll = (
    section: { hrefText?: LocalizedString } | undefined,
    labelKey: keyof typeof lbl,
  ) => (section?.hrefText !== undefined ? t(section.hrefText, locale, fallback) : r(labelKey));
  return {
    placeholder: cfg.searchPlaceholder
      ? t(cfg.searchPlaceholder, locale, fallback)
      : r('placeholder'),
    allResults: r('allResults'),
    courses: r('courses'),
    blog: r('blog'),
    news: r('news'),
    faq: r('faq'),
    documents: r('documents'),
    pages: r('pages'),
    employees: r('employees'),
    seeAllCourses: seeAll(cfg.courses, 'seeAllCourses'),
    seeAllBlog: seeAll(cfg.blog, 'seeAllBlog'),
    seeAllNews: seeAll(cfg.news, 'seeAllNews'),
    seeAllFaq: seeAll(cfg.faq, 'seeAllFaq'),
    seeAllDocuments: seeAll(cfg.documents, 'seeAllDocuments'),
    seeAllPages: seeAll(cfg.pages, 'seeAllPages'),
    seeAllEmployees: seeAll(cfg.employees, 'seeAllEmployees'),
    noResults: r('noResults'),
    typeToSearch: r('typeToSearch'),
    searching: r('searching'),
    close: r('close'),
    clearSearch: r('clearSearch'),
  };
}

/** "All categories" option label — not admin-configurable, mirrors the
 *  other built-in suggest-mode strings in {@link tr}. */
function allCategoriesLabel(locale: string): string {
  return locale === 'tr' ? 'Tüm kategoriler' : 'All categories';
}

/** Category filter select — shown when {@code search.showCategories} is on
 *  and at least one category is configured. Selecting a category adds
 *  {@code filter.category} to the catalog search request. */
function CategoryFilterSelect({
  categories,
  value,
  onChange,
  locale,
  localeFallback,
}: {
  categories: SearchSectionConfig['categories'];
  value: string;
  onChange: (value: string) => void;
  locale: string;
  localeFallback: string;
}) {
  if (!categories || categories.length === 0) return null;
  return (
    <div style={{ padding: '8px 16px 0' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '7px 10px',
          fontSize: 13,
          fontFamily: 'inherit',
          borderRadius: 'var(--radius-md, 8px)',
          border: '1px solid var(--color-border-subtle, #e5e7eb)',
          background: 'var(--color-surface-card, #fff)',
          color: 'var(--color-text-primary, #0f1218)',
        }}
      >
        <option value="">{allCategoriesLabel(locale)}</option>
        {categories.map((c) => (
          <option key={c.value} value={c.value}>
            {t(c.label, locale, localeFallback)}
          </option>
        ))}
      </select>
    </div>
  );
}

// =============================================================================
// Props
// =============================================================================

export interface LiveSearchBoxProps {
  /** Tenant slug used in {@code /search?q=} navigation. */
  tenantSlug: string;
  /** Display mode — pill in the header vs full overlay modal. */
  variant: 'inline' | 'overlay';
  /** Overlay-only: whether the modal is open. Parent owns this state. */
  open?: boolean;
  /** Overlay-only: called when the user closes the overlay. */
  onClose?: () => void;
  /**
   * Inline-only: when true, clicking the inline input opens it as a full
   * {@link SbCommandDialog} instead of showing a dropdown below.
   */
  expandToDialog?: boolean;
  /** Placeholder text. Overridden by catalog labels when in catalog mode. */
  placeholder?: string;
  /** Visual override — inverse (dark-chrome). */
  inverse?: boolean;
  /** Visual override — flush (no border, just bottom rule). */
  flush?: boolean;
  /** Stretch to fill parent width. */
  fillWidth?: boolean;
  /** Preferred pill width in px. Overrides the built-in default. A
   *  preference, not a floor — shrinks under a tight parent instead of
   *  overflowing it. Ignored when {@code fillWidth} is set. */
  width?: number;
  // Suggest-mode only -------------------------------------------------------
  kind?: string;
  resultsHref?: string;
  recentsScope?: string;
  // Catalog mode (from shell.search) ----------------------------------------
  /** When set, switches to catalog API mode.  Source: {@code shell.search}. */
  catalogSearch?: SearchSectionConfig;
  /** Active locale — used for catalog label resolution. */
  locale?: string;
  /** Fallback locale (from {@code shell.i18n.default}). Default 'en'. */
  localeFallback?: string;
  /** Numeric tenant ID for the catalog search API path.
   *  Takes precedence over {@code catalogSearch.tenantId}.
   *  Hosts read this from their auth client: {@code client.getTenantId()}. */
  tenantId?: string | number;
  /** Full path to the catalog search proxy route, including any app base path.
   *  Defaults to '/api/catalog/search'. Pass BASEPATH + '/api/catalog/search'
   *  when the app is mounted at a sub-path (e.g. /lxp). */
  catalogApiPath?: string;
}

// =============================================================================
// Component
// =============================================================================

export function LiveSearchBox(props: LiveSearchBoxProps) {
  const {
    tenantSlug,
    variant,
    open = false,
    onClose,
    placeholder,
    kind,
    resultsHref,
    recentsScope,
    catalogSearch,
    locale = 'tr',
    localeFallback = 'tr',
    tenantId: _tenantId,
    catalogApiPath = '/api/catalog-search',
    expandToDialog = false,
  } = props;

  const router = useRouter();
  const source = useSearchSource();
  const messages = useMessages();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();

  // Static suggest-mode strings, resolved once (host message → built-in).
  const suggest = resolveSuggestLabels(messages, locale);

  // ── Shared state ────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  // expandToDialog mode: inline input is just a trigger, dialog owns the query
  const [dialogOpen, setDialogOpen] = useState(false);

  // ── Suggest-mode state ──────────────────────────────────────────────────
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [trending, setTrending] = useState<string[]>([]);
  const [recents, setRecents] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // ── Catalog-mode state ──────────────────────────────────────────────────
  const [catalogResults, setCatalogResults] = useState<CatalogResponse | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  /** Selected `filter.category` value — '' means "all categories". */
  const [selectedCategory, setSelectedCategory] = useState('');

  const dropdownVisible = variant === 'overlay' ? open : focused;

  // ── Recents key ─────────────────────────────────────────────────────────
  const recentsKey = recentsScope
    ? `${RECENTS_STORAGE_KEY_PREFIX}${tenantSlug}:${recentsScope}`
    : `${RECENTS_STORAGE_KEY_PREFIX}${tenantSlug}`;

  // ── Suggest-mode effects ─────────────────────────────────────────────────
  useEffect(() => {
    if (catalogSearch) return;
    let local: string[] = [];
    try {
      const raw = window.localStorage.getItem(recentsKey);
      if (raw) local = JSON.parse(raw) as string[];
    } catch {
      /* ignore */
    }
    setRecents(local);
    if (source) {
      try {
        const fromSource = source.recents({ limit: MAX_RECENTS_DISPLAYED });
        if (fromSource.length > 0) setRecents((cur) => mergeQueries(cur, fromSource));
      } catch {
        /* ignore */
      }
    }
  }, [recentsKey, source, catalogSearch]);

  useEffect(() => {
    if (catalogSearch) return;
    try {
      window.localStorage.setItem(
        recentsKey,
        JSON.stringify(recents.slice(0, MAX_RECENTS_DISPLAYED)),
      );
    } catch {
      /* ignore */
    }
  }, [recents, recentsKey, catalogSearch]);

  useEffect(() => {
    if (catalogSearch || !source) return;
    const ctrl = new AbortController();
    source
      .trending({ kind, signal: ctrl.signal })
      .then(setTrending)
      .catch(() => {});
    return () => ctrl.abort();
  }, [source, kind, catalogSearch]);

  useEffect(() => {
    if (catalogSearch) return;
    const trimmed = query.trim();
    if (!trimmed || !source) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      source
        .suggest({ query: trimmed, kind, signal: ctrl.signal })
        .then(setSuggestions)
        .catch(() => {});
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, source, kind, catalogSearch]);

  // ── Catalog-mode effect ──────────────────────────────────────────────────
  useEffect(() => {
    if (!catalogSearch) return;
    const trimmed = query.trim();
    if (!trimmed) {
      setCatalogResults(null);
      setCatalogLoading(false);
      return;
    }
    setCatalogLoading(true);
    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const resp = await fetch(catalogApiPath, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            maxResults: catalogSearch.maxResults ?? 3,
            filter: {
              searchTerm: trimmed,
              ...(selectedCategory ? { category: selectedCategory } : {}),
            },
            sortField: 'recent',
            sortOrder: 'ASCENDING',
          }),
          signal: ctrl.signal,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = (await resp.json()) as CatalogResponse;
        setCatalogResults(data);
      } catch {
        /* aborted or network error */
      } finally {
        setCatalogLoading(false);
      }
    }, CATALOG_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [query, catalogSearch, locale, selectedCategory]);

  // Reset tab when results change
  useEffect(() => {
    setActiveTab('all');
  }, [catalogResults]);

  // Reset catalog state when overlay closes
  useEffect(() => {
    if (variant === 'overlay' && !open && catalogSearch) {
      setQuery('');
      setCatalogResults(null);
      setActiveTab('all');
      setSelectedCategory('');
    }
  }, [variant, open, catalogSearch]);

  // ── Suggest-mode row list ────────────────────────────────────────────────
  type Row = { kind: 'suggestion'; data: SearchSuggestion } | { kind: 'query'; data: string };
  const rows: Row[] = useMemo(() => {
    if (catalogSearch) return [];
    const out: Row[] = [];
    if (query.trim()) {
      for (const s of suggestions) out.push({ kind: 'suggestion', data: s });
    } else {
      for (const r of recents) out.push({ kind: 'query', data: r });
      for (const trend of trending) {
        if (!recents.some((r) => r.toLowerCase() === trend.toLowerCase()))
          out.push({ kind: 'query', data: trend });
      }
    }
    return out;
  }, [query, suggestions, recents, trending, catalogSearch]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [rows.length]);

  // ── Shared actions ───────────────────────────────────────────────────────
  const closeDropdown = useCallback(() => {
    if (variant === 'overlay') onClose?.();
    else {
      setFocused(false);
      inputRef.current?.blur();
    }
  }, [variant, onClose]);

  const submitQuery = useCallback(
    (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      // Catalog mode: Enter / "see full search results" lands on the
      // course catalog page carrying the query (e.g. /lxp/catalog?sort=popular&q=a1).
      if (catalogSearch) {
        closeDropdown();
        router.push(catalogSearchHref(trimmed, catalogSearch.courses?.href));
        return;
      }
      setRecents((cur) => mergeQueries([trimmed], cur));
      source?.pushRecent(trimmed);
      closeDropdown();
      const base = resultsHref ?? `/${encodeURIComponent(tenantSlug)}/search`;
      router.push(`${base}?q=${encodeURIComponent(trimmed)}`);
    },
    [tenantSlug, closeDropdown, router, resultsHref, source, catalogSearch],
  );

  const navigateToHit = useCallback(
    (href: string, q: string) => {
      if (q.trim()) {
        setRecents((cur) => mergeQueries([q.trim()], cur));
        source?.pushRecent(q.trim());
      }
      closeDropdown();
      router.push(href);
    },
    [closeDropdown, router, source],
  );

  const clearRecents = useCallback(() => {
    setRecents([]);
    try {
      window.localStorage.removeItem(recentsKey);
    } catch {
      /* ignore */
    }
    source?.clearRecents?.();
  }, [recentsKey, source]);

  // ── Keyboard nav ─────────────────────────────────────────────────────────
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!catalogSearch && activeIndex >= 0 && activeIndex < rows.length) {
        const row = rows[activeIndex]!;
        if (row.kind === 'suggestion') navigateToHit(row.data.href, query);
        else submitQuery(row.data);
      } else {
        submitQuery(query);
      }
      return;
    }
    if (!catalogSearch) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (rows.length) setActiveIndex((i) => (i + 1 >= rows.length ? 0 : i + 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (rows.length) setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
      }
    }
  };

  // ── Catalog resolved labels ──────────────────────────────────────────────
  const catalogLabels = catalogSearch
    ? resolveLabels(catalogSearch, locale, localeFallback, messages)
    : null;

  // Placeholder precedence: an explicit operator-authored placeholder
  // (header.searchPlaceholder, passed as the `placeholder` prop) always
  // wins — otherwise it silently loses to the catalog default whenever a
  // search/catalog config exists. Falls back to the catalog label (catalog
  // mode) or the built-in suggest default.
  const effectivePlaceholder =
    placeholder ?? (catalogSearch ? catalogLabels!.placeholder : suggest.placeholder);

  // ── Catalog visible tabs ──────────────────────────────────────────────────
  const catalogTabs =
    catalogSearch && catalogResults
      ? buildCatalogTabs(catalogSearch, catalogResults, catalogLabels!)
      : [];

  const effectiveTab: TabId =
    catalogTabs.length === 0 || activeTab === 'all' || catalogTabs.some((t) => t.id === activeTab)
      ? activeTab
      : 'all';

  // SbCommandTabBar expects SbCommandTab[] which has optional count
  const sbTabs: SbCommandTab[] = catalogTabs.map((t) => ({
    id: t.id,
    label: t.label,
    count: t.count,
  }));

  // =========================================================================
  // INLINE VARIANT
  // =========================================================================
  if (variant === 'inline') {
    // expandToDialog: inline input is a trigger that opens the overlay dialog
    if (expandToDialog) {
      const handleDialogClose = () => {
        setDialogOpen(false);
        setQuery('');
        setCatalogResults(null);
        setActiveTab('all');
      };

      return (
        <>
          {/* Trigger pill — read-only, opens dialog on click/focus */}
          <div
            ref={containerRef}
            // className="lms-search-inline"
            style={{
              position: 'relative',
              width: props.fillWidth ? '100%' : 'auto',
              minWidth: 0,
            }}
          >
            <SbCommandInput
              inputRef={inputRef}
              value=""
              onChange={() => {}}
              onFocus={() => {
                inputRef.current?.blur();
                setDialogOpen(true);
              }}
              placeholder={effectivePlaceholder}
              // This trigger's role="combobox" (baked into SbCommandInput)
              // needs aria-expanded to reflect the popup it controls — here
              // that's the full-screen SbCommandDialog, not a listbox.
              ariaExpanded={dialogOpen}
              size="sm"
              inverse={props.inverse}
              flush={props.flush}
              fillWidth={props.fillWidth}
              width={props.width}
            />
          </div>

          {/* Full overlay dialog */}
          <SbCommandDialog open={dialogOpen} onClose={handleDialogClose}>
            <SbCommandInput
              value={query}
              onChange={setQuery}
              onKeyDown={onKeyDown}
              placeholder={effectivePlaceholder}
              ariaControls={listboxId}
              ariaExpanded={catalogSearch ? !!query.trim() : rows.length > 0}
              size="lg"
              loading={catalogSearch ? catalogLoading : false}
              onClear={query ? () => setQuery('') : undefined}
              clearLabel={catalogSearch ? catalogLabels!.clearSearch : suggest.clearSearch}
              closeLabel={catalogSearch ? catalogLabels!.close : undefined}
              onClose={handleDialogClose}
            />

            {catalogSearch ? (
              <>
                <SbCommandTabBar
                  tabs={sbTabs}
                  activeTab={effectiveTab}
                  onTabChange={(id) => setActiveTab(id as TabId)}
                />
                {catalogSearch.showCategories && (
                  <CategoryFilterSelect
                    categories={catalogSearch.categories}
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    locale={locale}
                    localeFallback={localeFallback}
                  />
                )}
                <div id={listboxId} style={{ overflowY: 'auto', flex: 1 }}>
                  <CatalogBody
                    query={query}
                    loading={catalogLoading}
                    results={catalogResults}
                    labels={catalogLabels!}
                    cfg={catalogSearch}
                    activeTab={effectiveTab}
                    tenantSlug={tenantSlug}
                    navigate={(href) => {
                      handleDialogClose();
                      router.push(href);
                    }}
                  />
                </div>
              </>
            ) : (
              <div id={listboxId} style={{ overflowY: 'auto', flex: 1 }}>
                {rows.length > 0 && (
                  <SuggestDropdown
                    id={listboxId}
                    rows={rows}
                    activeIndex={activeIndex}
                    query={query}
                    recentsCount={query.trim() ? 0 : recents.length}
                    onSelectRow={(row) => {
                      if (row.kind === 'suggestion') navigateToHit(row.data.href, query);
                      else submitQuery(row.data);
                    }}
                    onClearRecents={recents.length > 0 && !query.trim() ? clearRecents : undefined}
                    anchored="modal"
                    labels={suggest}
                  />
                )}
              </div>
            )}
          </SbCommandDialog>
        </>
      );
    }

    const isCatalogDropdownOpen = focused && !!query.trim() && catalogSearch !== undefined;
    const isSuggestDropdownOpen = focused && dropdownVisible && !catalogSearch;

    return (
      <div
        ref={containerRef}
        className="lms-search-inline"
        style={{
          position: 'relative',
          width: props.fillWidth ? '100%' : 'auto',
          minWidth: 0,
        }}
      >
        <SbCommandInput
          inputRef={inputRef}
          value={query}
          onChange={setQuery}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          placeholder={effectivePlaceholder}
          ariaControls={listboxId}
          ariaExpanded={
            catalogSearch ? isCatalogDropdownOpen : isSuggestDropdownOpen && rows.length > 0
          }
          size="sm"
          inverse={props.inverse}
          flush={props.flush}
          fillWidth={props.fillWidth}
          width={props.width}
          loading={catalogSearch ? catalogLoading : false}
          onClear={query ? () => setQuery('') : undefined}
          clearLabel={catalogSearch ? catalogLabels!.clearSearch : suggest.clearSearch}
        />

        {/* Catalog mode dropdown */}
        {catalogSearch && isCatalogDropdownOpen && (
          <div
            id={listboxId}
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              // Scales with the input's own configured width (a wider
              // input implies a wider result panel is wanted) rather than
              // a flat value — 360px floor keeps it readable even for the
              // 'compact'/'default' presets, which are narrower than that.
              minWidth: Math.max((props.width ?? 260) * 1.6, 360),
              maxHeight: 'min(78vh, 760px)',
              overflowY: 'auto',
              background: 'var(--color-surface-card, #fff)',
              border: '1px solid var(--color-border-subtle, #e5e7eb)',
              borderRadius: 12,
              boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
              zIndex: 50,
            }}
          >
            <SbCommandTabBar
              tabs={sbTabs}
              activeTab={effectiveTab}
              onTabChange={(id) => setActiveTab(id as TabId)}
            />
            {catalogSearch.showCategories && (
              <CategoryFilterSelect
                categories={catalogSearch.categories}
                value={selectedCategory}
                onChange={setSelectedCategory}
                locale={locale}
                localeFallback={localeFallback}
              />
            )}
            <CatalogBody
              query={query}
              loading={catalogLoading}
              results={catalogResults}
              labels={catalogLabels!}
              cfg={catalogSearch}
              activeTab={effectiveTab}
              tenantSlug={tenantSlug}
              navigate={(href) => {
                setFocused(false);
                router.push(href);
              }}
            />
          </div>
        )}

        {/* Suggest mode dropdown */}
        {!catalogSearch && isSuggestDropdownOpen && (
          <SuggestDropdown
            id={listboxId}
            rows={rows}
            activeIndex={activeIndex}
            query={query}
            recentsCount={query.trim() ? 0 : recents.length}
            onSelectRow={(row) => {
              if (row.kind === 'suggestion') navigateToHit(row.data.href, query);
              else submitQuery(row.data);
            }}
            onClearRecents={recents.length > 0 && !query.trim() ? clearRecents : undefined}
            anchored="below"
            labels={suggest}
          />
        )}
      </div>
    );
  }

  // =========================================================================
  // OVERLAY VARIANT — uses SbCommandDialog
  // =========================================================================

  return (
    <SbCommandDialog open={open} onClose={onClose ?? (() => {})}>
      <SbCommandInput
        inputRef={inputRef}
        value={query}
        onChange={setQuery}
        onKeyDown={onKeyDown}
        placeholder={effectivePlaceholder}
        ariaControls={listboxId}
        ariaExpanded={catalogSearch ? !!query.trim() : rows.length > 0}
        size="lg"
        loading={catalogSearch ? catalogLoading : false}
        onClear={query ? () => setQuery('') : undefined}
        clearLabel={catalogSearch ? catalogLabels!.clearSearch : suggest.clearSearch}
        closeLabel={catalogSearch ? catalogLabels!.close : undefined}
        onClose={onClose}
      />

      {catalogSearch ? (
        <>
          <SbCommandTabBar
            tabs={sbTabs}
            activeTab={effectiveTab}
            onTabChange={(id) => setActiveTab(id as TabId)}
          />
          {catalogSearch.showCategories && (
            <CategoryFilterSelect
              categories={catalogSearch.categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              locale={locale}
              localeFallback={localeFallback}
            />
          )}
          <div id={listboxId} style={{ overflowY: 'auto', flex: 1 }}>
            <CatalogBody
              query={query}
              loading={catalogLoading}
              results={catalogResults}
              labels={catalogLabels!}
              cfg={catalogSearch}
              activeTab={effectiveTab}
              tenantSlug={tenantSlug}
              navigate={(href) => {
                onClose?.();
                router.push(href);
              }}
            />
          </div>
        </>
      ) : (
        <div id={listboxId} style={{ overflowY: 'auto', flex: 1 }}>
          {rows.length > 0 && (
            <SuggestDropdown
              id={listboxId}
              rows={rows}
              activeIndex={activeIndex}
              query={query}
              recentsCount={query.trim() ? 0 : recents.length}
              onSelectRow={(row) => {
                if (row.kind === 'suggestion') navigateToHit(row.data.href, query);
                else submitQuery(row.data);
              }}
              onClearRecents={recents.length > 0 && !query.trim() ? clearRecents : undefined}
              anchored="modal"
              labels={suggest}
            />
          )}
        </div>
      )}
    </SbCommandDialog>
  );
}

// =============================================================================
// CatalogBody — pure result rendering, no search/API logic
// =============================================================================

/**
 * One descriptor per catalog content type. Drives BOTH the tab bar
 * ({@link buildCatalogTabs}) and the result list ({@link CatalogBody}) so
 * the seven near-identical sections stay in sync from a single source.
 * Order here is the render order and the tab order.
 */
type SectionCfgKey = 'courses' | 'blog' | 'news' | 'faq' | 'documents' | 'pages' | 'employees';
type CatalogDocKey = keyof CatalogResponse['expandedDocuments'];

interface CatalogSectionSpec {
  /** Tab id / API {@code docType}. */
  id: Exclude<TabId, 'all'>;
  /** Key into {@code results.expandedDocuments}. */
  docKey: CatalogDocKey;
  /** Key into {@link SearchSectionConfig} for {@code show}/{@code href}. */
  cfgKey: SectionCfgKey;
  /** {@link ResolvedLabels} key for the group heading. */
  labelKey: keyof ResolvedLabels;
  /** {@link ResolvedLabels} key for the "see all" link text. */
  seeAllKey: keyof ResolvedLabels;
  /** Default visibility when the tenant hasn't configured {@code show}. */
  defaultShow: boolean;
  thumbnailShape: 'wide' | 'square';
  /** Fallback icon shown when a doc has no thumbnail. */
  icon?: ReactNode;
  label: (doc: CatalogDoc) => string;
  sublabel?: (doc: CatalogDoc) => string | undefined;
  thumbnail?: (doc: CatalogDoc) => string | undefined;
  /** Row navigation target. Omit to make rows non-navigable (faq / documents). */
  itemHref?: (doc: CatalogDoc, slug: string, cfg: SearchSectionConfig) => string;
  seeAllHref: (slug: string, cfg: SearchSectionConfig) => string;
  /** Per-tab separator override. Omit → {@code SbCommandGroup} default (true). */
  separator?: (activeTab: TabId) => boolean;
}

const CATALOG_SECTIONS: CatalogSectionSpec[] = [
  {
    id: 'course',
    docKey: 'course',
    cfgKey: 'courses',
    labelKey: 'courses',
    seeAllKey: 'seeAllCourses',
    defaultShow: true,
    thumbnailShape: 'wide',
    label: (d) => d.title ?? '',
    sublabel: (d) => d.description,
    thumbnail: (d) => d.thumbnail ?? d.image,
    // Course rows open the friendly detail route: /lxp/course/{title-id}.
    itemHref: (d) => courseDetailHref(d.title, d.id),
    seeAllHref: (_slug, cfg) => cfg.courses?.href ?? `${CATALOG_BASE}?sort=popular`,
    separator: (activeTab) => activeTab !== 'all',
  },
  {
    id: 'blog',
    docKey: 'blog',
    cfgKey: 'blog',
    labelKey: 'blog',
    seeAllKey: 'seeAllBlog',
    defaultShow: false,
    thumbnailShape: 'wide',
    label: (d) => d.title ?? '',
    sublabel: (d) => d.description,
    thumbnail: (d) => d.thumbnail ?? d.image,
    itemHref: (d, slug, cfg) =>
      cfg.blog?.href ? `${cfg.blog.href}/${d.id}` : `/${slug}/blog/${d.id}`,
    seeAllHref: (slug, cfg) => cfg.blog?.href ?? `/${slug}/blog`,
  },
  {
    id: 'news',
    docKey: 'news',
    cfgKey: 'news',
    labelKey: 'news',
    seeAllKey: 'seeAllNews',
    defaultShow: true,
    thumbnailShape: 'wide',
    label: (d) => d.title ?? '',
    sublabel: (d) => d.description,
    thumbnail: (d) => d.thumbnail ?? d.image,
    itemHref: (d, slug, cfg) =>
      cfg.news?.href ? `${cfg.news.href}/${d.id}` : `/${slug}/news/${d.id}`,
    seeAllHref: (slug, cfg) => cfg.news?.href ?? `/${slug}/news`,
  },
  {
    id: 'faq',
    docKey: 'faq',
    cfgKey: 'faq',
    labelKey: 'faq',
    seeAllKey: 'seeAllFaq',
    defaultShow: false,
    thumbnailShape: 'square',
    icon: <QuestionIcon />,
    label: (d) => d.question ?? d.title ?? '',
    seeAllHref: (slug, cfg) => cfg.faq?.href ?? `/${slug}/faq`,
  },
  {
    id: 'document',
    docKey: 'document',
    cfgKey: 'documents',
    labelKey: 'documents',
    seeAllKey: 'seeAllDocuments',
    defaultShow: false,
    thumbnailShape: 'square',
    icon: <DocIcon />,
    label: (d) => d.name ?? d.title ?? '',
    seeAllHref: (slug, cfg) => cfg.documents?.href ?? `/${slug}/documents`,
  },
  {
    id: 'page',
    docKey: 'page',
    cfgKey: 'pages',
    labelKey: 'pages',
    seeAllKey: 'seeAllPages',
    defaultShow: false,
    thumbnailShape: 'square',
    icon: <PageIcon />,
    label: (d) => d.title ?? '',
    sublabel: (d) => d.description,
    itemHref: (d, _slug, cfg) =>
      cfg.pages?.href ? `${cfg.pages.href}/${d.key ?? d.id}` : `/${d.key ?? d.id}`,
    seeAllHref: (slug, cfg) => cfg.pages?.href ?? `/${slug}/pages`,
  },
  {
    id: 'employee',
    docKey: 'employee',
    cfgKey: 'employees',
    labelKey: 'employees',
    seeAllKey: 'seeAllEmployees',
    defaultShow: false,
    thumbnailShape: 'square',
    label: (d) => [d.name, d.surname].filter(Boolean).join(' ') || d.title || '',
    sublabel: (d) => d.description,
    thumbnail: (d) => d.thumbnail ?? d.image ?? d.avatarUrl,
    itemHref: (d, slug, cfg) =>
      cfg.employees?.href ? `${cfg.employees.href}/${d.id}` : `/${slug}/employees/${d.id}`,
    seeAllHref: (slug, cfg) => cfg.employees?.href ?? `/${slug}/employees`,
  },
];

/** A section is live when configured (or defaulted) to show AND its API
 *  bucket came back with at least one document. */
function sectionIsVisible(
  spec: CatalogSectionSpec,
  cfg: SearchSectionConfig,
  results: CatalogResponse,
): CatalogSection | null {
  const show = cfg[spec.cfgKey]?.show ?? spec.defaultShow;
  const section = results.expandedDocuments?.[spec.docKey];
  if (!show || !section || section.documents.length === 0) return null;
  return section;
}

function buildCatalogTabs(
  cfg: SearchSectionConfig,
  results: CatalogResponse,
  labels: ResolvedLabels,
): TabDef[] {
  const tabs: TabDef[] = [];
  let total = 0;

  for (const spec of CATALOG_SECTIONS) {
    const section = sectionIsVisible(spec, cfg, results);
    if (!section) continue;
    tabs.push({ id: spec.id, label: labels[spec.labelKey], count: section.totalRecords });
    total += section.totalRecords;
  }

  if (tabs.length === 0) return [];
  return [{ id: 'all', label: labels.allResults, count: total }, ...tabs];
}

function CatalogBody({
  query,
  loading,
  results,
  labels,
  cfg,
  activeTab,
  tenantSlug,
  navigate,
}: {
  query: string;
  loading: boolean;
  results: CatalogResponse | null;
  labels: ResolvedLabels;
  cfg: SearchSectionConfig;
  activeTab: TabId;
  tenantSlug: string;
  navigate: (href: string) => void;
}) {
  const hasQuery = !!query.trim();
  const slug = encodeURIComponent(tenantSlug);

  if (!hasQuery) return <SbCommandEmpty>{labels.typeToSearch}</SbCommandEmpty>;
  if (loading && !results) return <SbCommandEmpty>{labels.searching}</SbCommandEmpty>;

  const hasResults =
    results && Object.values(results.expandedDocuments).some((s) => s && s.documents.length > 0);
  if (!hasResults && !loading) return <SbCommandEmpty>{labels.noResults}</SbCommandEmpty>;
  if (!results) return null;

  return (
    <>
      {CATALOG_SECTIONS.map((spec) => {
        if (!shouldShow(activeTab, spec.id)) return null;
        const section = sectionIsVisible(spec, cfg, results);
        if (!section) return null;
        return (
          <SbCommandGroup
            key={spec.id}
            heading={labels[spec.labelKey]}
            separator={spec.separator?.(activeTab)}
          >
            {section.documents.map((doc) => (
              <SbCommandItem
                key={doc.id}
                label={spec.label(doc)}
                sublabel={spec.sublabel?.(doc)}
                thumbnail={spec.thumbnail?.(doc)}
                icon={spec.icon}
                thumbnailShape={spec.thumbnailShape}
                onSelect={spec.itemHref ? () => navigate(spec.itemHref!(doc, slug, cfg)) : () => {}}
              />
            ))}
            {section.totalRecords > section.documents.length && (
              <SbCommandSeeAll
                label={labels[spec.seeAllKey]}
                href={
                  spec.id === 'course'
                    ? catalogSearchHref(query, cfg.courses?.href)
                    : spec.seeAllHref(slug, cfg)
                }
                onNavigate={navigate}
              />
            )}
          </SbCommandGroup>
        );
      })}
    </>
  );
}

function shouldShow(activeTab: TabId, sectionId: TabId): boolean {
  return activeTab === 'all' || activeTab === sectionId;
}

// =============================================================================
// SuggestDropdown — suggest-mode result list (recents / trending / suggestions)
// =============================================================================

function SuggestDropdown({
  id,
  rows,
  activeIndex,
  query,
  recentsCount,
  onSelectRow,
  onClearRecents,
  anchored,
  labels,
}: {
  id: string;
  rows: Array<{ kind: 'suggestion'; data: SearchSuggestion } | { kind: 'query'; data: string }>;
  activeIndex: number;
  query: string;
  recentsCount: number;
  onSelectRow: (
    row: { kind: 'suggestion'; data: SearchSuggestion } | { kind: 'query'; data: string },
  ) => void;
  onClearRecents?: () => void;
  anchored: 'below' | 'modal';
  labels: SuggestLabels;
}) {
  const isModal = anchored === 'modal';
  const wrapStyle: CSSProperties = isModal
    ? {
        maxHeight: 'min(60vh, 540px)',
        overflowY: 'auto',
        background: 'var(--color-surface-card, #fff)',
      }
    : {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        right: 0,
        minWidth: 320,
        maxHeight: 480,
        overflowY: 'auto',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-border-subtle, #e5e7eb)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
        zIndex: 50,
      };

  const showHeaders = !query.trim();
  const trendingStart = recentsCount;

  return (
    <div id={id} role="listbox" style={wrapStyle}>
      {rows.length === 0 ? (
        <div
          style={{
            padding: '20px 16px',
            textAlign: 'center',
            color: 'var(--color-text-secondary, #5f6675)',
            fontSize: 13,
          }}
        >
          {query.trim() ? labels.noSuggestions(query) : labels.startTyping}
        </div>
      ) : (
        <>
          {showHeaders && recentsCount > 0 && (
            <DropdownHeader
              label={labels.recentSearches}
              actionLabel={onClearRecents ? labels.clearRecents : undefined}
              onAction={onClearRecents}
            />
          )}
          {rows.map((row, i) => {
            const active = i === activeIndex;
            return (
              <div key={row.kind === 'suggestion' ? `s:${row.data.id}` : `q:${row.data}:${i}`}>
                {showHeaders && i === trendingStart && trendingStart < rows.length && (
                  <DropdownHeader label={labels.trending} />
                )}
                <DropdownRow row={row} active={active} onClick={() => onSelectRow(row)} />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function DropdownHeader({
  label,
  actionLabel,
  onAction,
}: {
  label: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px 6px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 'var(--font-tracking-wider, 0.06em)',
        textTransform: 'uppercase',
        color: 'var(--color-text-tertiary, #71717a)',
      }}
    >
      <span>{label}</span>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          onMouseDown={(e) => e.preventDefault()}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--color-brand-500, #3b5bdb)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 'var(--font-tracking-wider, 0.04em)',
            textTransform: 'uppercase',
            padding: 0,
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function DropdownRow({
  row,
  active,
  onClick,
}: {
  row: { kind: 'suggestion'; data: SearchSuggestion } | { kind: 'query'; data: string };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '10px 14px',
        border: 'none',
        background: active ? 'var(--color-neutral-100, #f3f4f6)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 14,
        color: 'var(--color-text-primary, #0f1218)',
      }}
    >
      {row.kind === 'suggestion' ? (
        <>
          <SuggestThumbnail src={row.data.thumbnail} kind={row.data.kind} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.data.title}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--color-text-secondary, #5f6675)',
                textTransform: 'capitalize',
              }}
            >
              {row.data.kind}
            </div>
          </div>
        </>
      ) : (
        <>
          <SearchSmIcon />
          <span
            style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {row.data}
          </span>
        </>
      )}
    </button>
  );
}

function SuggestThumbnail({ src, kind }: { src?: string; kind: string }) {
  const isSquare = kind === 'channel';
  const w = isSquare ? 28 : 48;
  return (
    <div
      style={{
        flexShrink: 0,
        width: w,
        height: 28,
        borderRadius: isSquare ? '50%' : 4,
        background: 'var(--color-surface-muted, #ececef)',
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  );
}

// =============================================================================
// Icons
// =============================================================================

function SearchSmIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text-tertiary, #71717a)"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <circle cx={11} cy={11} r={8} />
      <line x1={21} y1={21} x2={16.65} y2={16.65} />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text-secondary,#5f6675)"
      strokeWidth={2}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  );
}

function PageIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text-secondary,#5f6675)"
      strokeWidth={2}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-text-secondary,#5f6675)"
      strokeWidth={2}
    >
      <circle cx={12} cy={12} r={10} />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1={12} y1={17} x2={12.01} y2={17} />
    </svg>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function mergeQueries(primary: string[], secondary: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const q of [...primary, ...secondary]) {
    const key = q.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(q);
    if (out.length >= MAX_RECENTS_DISPLAYED) break;
  }
  return out;
}
